import { Box, IconButton, InputAdornment, Link, List, TextField, Typography } from '@material-ui/core'
import { FunctionComponent, useState } from 'react'
import { BackendUser, UserRoles } from 'common/types'
import { useScreenSizes } from 'hooks/use-screen-sizes/useScreenSizes'
import { ShowTimerText } from 'pages/settings/components/ShowTimerText'
import passwordValidator from 'password-validator'
//@ts-ignore
import styled, { useTheme } from 'styled-components'
import validator from 'validator'
import { PasswordCheckItem } from './PasswordCheckItem'
import { Visibility, VisibilityOff } from '@material-ui/icons'
import { Button } from 'common/atoms/Button/Button'

interface SignUpEmailProps {
  onSubmit: Function
  username: string
  password: string
  setUsername: Function
  setPassword: Function
  role: UserRoles
  title?: string
  user: BackendUser
  confirmEmail?: Function
  errorCount?: number
  setErrorCount?: Function
  setShowTicketModal?: Function
  success?: boolean
  setSuccess?: Function
  setForwardingUrl?: Function
  timer: number
  setTimer: Function
  errorFlag: boolean
  setErrorFlag: Function
  errorMessage: string
  setErrorMessage: Function
  clearItem: Function
  getItem: Function
  showConfirmEmailModal: boolean
  setShowConfirmEmailModal: Function
  isLocked: boolean
  setIsLocked: Function
  handleUsernameChange: Function
  resendConfirmationCode: Function
}

const StyledTextField = styled(TextField)`
  input:disabled {
    color: ${props => props.theme.colors.accent} !important;
    cursor: default;
  }
`

export const SignUpEmail: FunctionComponent<SignUpEmailProps> = ({
  username,
  password,
  role,
  setUsername,
  setPassword,
  onSubmit,
  user,
  confirmEmail,
  errorCount,
  setErrorCount,
  timer,
  setTimer,
  errorFlag,
  setErrorFlag,
  errorMessage,
  setErrorMessage,
  getItem,
  clearItem,
  showConfirmEmailModal,
  setShowConfirmEmailModal,
  isLocked,
  setIsLocked,
  handleUsernameChange,
  resendConfirmationCode
}) => {
  const [confirmPassword, setConfirmPassword] = useState('')
  const [usernameValid, setUsernameValid] = useState(!username ? false : validator.isEmail(username))
  const [sendCode, setSendCode] = useState(false)
  const [confirmPasswordValid, setConfirmPasswordValid] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const [code, setCode] = useState('')
  const theme = useTheme()
  const { isDevice } = useScreenSizes()

  const passwordChecks = [
    {
      label: 'Minimum eight (8) characters',
      isEnabled: new passwordValidator().is().min(8).validate(password)
    },
    {
      isEnabled: new passwordValidator().is().max(100).validate(password)
    },
    {
      label: 'One(1) Uppercase character',
      isEnabled: new passwordValidator().has().uppercase(1).validate(password)
    },
    {
      label: 'One(1) Lowercase character',
      isEnabled: new passwordValidator().has().lowercase().validate(password)
    },
    {
      label: 'One(1) Numerical digit',
      isEnabled: new passwordValidator().has().digits(1).validate(password)
    },
    {
      isEnabled: new passwordValidator().has().not().spaces().validate(password)
    },
    {
      label: 'One(1) Special character (symbol)',
      isEnabled: new passwordValidator().has().symbols(1).validate(password)
    }
  ]

  const onUsernameChange = (e: any) => {
    const value = e.target.value
    setUsername(value)
    const test = validator.isEmail(value)
    setUsernameValid(test)
  }

  const passwordValid = !passwordChecks.find(check => !check.isEnabled)

  const onPasswordChange = (e: any) => {
    const value = e.target.value
    setPassword(value)
    const test2 = validator.equals(value, confirmPassword)
    setConfirmPasswordValid(test2)
  }

  const onConfirmPasswordChange = (e: any) => {
    const value = e.target.value
    setConfirmPassword(value)
    const test = validator.equals(value, password)
    setConfirmPasswordValid(test)
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      width="100%"
      alignItems={'center'}
      padding={isDevice ? '0 2em' : '0 7em'}
    >
      <Box m={isDevice ? '1em 0 0.5em 0' : '1em 0 '} width={isDevice ? '100%' : '30em'}>
        <StyledTextField
          disabled={sendCode || isLocked}
          type="email"
          value={username}
          onChange={e => onUsernameChange(e)}
          onBlur={e => handleUsernameChange(e.target.value)}
          placeholder={role === UserRoles.Partner ? 'Company Email' : 'Email'}
          variant="outlined"
          fullWidth
          color="primary"
          size="small"
        />
      </Box>
      {!usernameValid && username && (
        <Typography variant="body1" component="span">
          <Box color={theme.colors.error} m={isDevice ? '' : '-0.5em 0 0.5em 0'}>
            Username is Invalid
          </Box>
        </Typography>
      )}
      <Box m={isDevice ? '0.5em 0' : '0 0 1em 0'} width={isDevice ? '100%' : '30em'}>
        <StyledTextField
          disabled={sendCode || isLocked}
          value={password}
          type={showPassword ? 'text' : 'password'}
          onChange={e => onPasswordChange(e)}
          variant="outlined"
          placeholder="Password"
          fullWidth
          size="small"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? (
                    <Visibility style={{ color: theme.colors.accent }} />
                  ) : (
                    <VisibilityOff style={{ color: theme.colors.accent }} />
                  )}
                </IconButton>
              </InputAdornment>
            )
          }}
        />
      </Box>
      {!passwordValid && password && (
        <Box
          m={isDevice ? '' : ' 0 0 1em 0'}
          color={theme.colors.accent1}
          textAlign="justify"
          width={isDevice ? '100%' : '30em'}
        >
          <Typography variant="body1" component="span">
            Password does not meet the minimum requirements.
            <br />
            Password must contain:-
          </Typography>
          <List>
            {passwordChecks.map(check => {
              if (check.label) {
                return <PasswordCheckItem label={check.label} isEnabled={check.isEnabled} theme={theme} />
              }
            })}
          </List>
        </Box>
      )}
      <Box m={isDevice ? '0.5em 0' : '0 0 1em 0'} width={isDevice ? '100%' : '30em'}>
        <StyledTextField
          disabled={sendCode || isLocked}
          type={showConfirmPassword ? 'text' : 'password'}
          onChange={e => onConfirmPasswordChange(e)}
          onKeyPress={e => {
            if (e.key === 'Enter') {
              onSubmit()
            }
          }}
          value={confirmPassword}
          variant="outlined"
          placeholder="Confirm Password"
          size="small"
          fullWidth
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                  {showConfirmPassword ? (
                    <Visibility style={{ color: theme.colors.accent }} />
                  ) : (
                    <VisibilityOff style={{ color: theme.colors.accent }} />
                  )}
                </IconButton>
              </InputAdornment>
            )
          }}
        />
      </Box>

      {!confirmPasswordValid && confirmPassword && (
        <Typography variant="body1" component="span">
          <Box color={theme.colors.error}>Passwords do not match</Box>
        </Typography>
      )}

      <Box
        m={isDevice ? '1em 0' : '0 0 1em 0'}
        display="flex"
        justifyContent="space-between"
        width={isDevice ? '100%' : '30em'}
      >
        {/* @ts-ignore */}
        <Box width={isDevice ? '10em' : '15em'} mr="0.55em">
          <Button
            design='primary'
            disabled={!(usernameValid && passwordValid && confirmPasswordValid) || sendCode}
            onClick={() => {
              onSubmit()
              setSendCode(true)
              setShowConfirmEmailModal(true)
            }}
            style={{ fontWeight: 'bold' }}
            fullWidth
          >
            <Typography variant="body1" component="span">
              <Box fontWeight="bold" fontSize="0.8em" whiteSpace={'nowrap'}>
                {isDevice ? 'Send Code' : 'Send Confirmation Code'}
              </Box>
            </Typography>
          </Button>
        </Box>
        <Box>
          <Box width="100%">
            <TextField
              placeholder="Enter code"
              size="small"
              variant="outlined"
              fullWidth
              type="text"
              disabled={timer === 0 || !showConfirmEmailModal}
              onChange={e => {
                setCode(e.currentTarget.value)
                setErrorFlag(false)
              }}
            />
          </Box>
          {sendCode && (
            <Typography variant="caption" component="span">
              <Box
                color={theme.colors.accent1}
                display="flex"
                justifyContent="flex-start"
                ml={isDevice ? '-6em' : '-2.5em'}
              >
                A confirmation code has been sent to your email
              </Box>
            </Typography>
          )}
          {errorCount > 0 && errorFlag && (
            <Typography component="span">
              <Box display="flex" justifyContent="flex-start" color={theme.colors.error}>
                {errorMessage === 'Code Expired' ? 'Code Expired' : 'Invalid Code'}
              </Box>
            </Typography>
          )}
          {showConfirmEmailModal && (
            <Box display="flex" justifyContent={timer === 0 ? 'flex-start' : 'flex-end'}>
              <ShowTimerText
                user={user}
                timer={timer}
                setTimer={setTimer}
                setErrorCount={setErrorCount}
                setErrorMessage={setErrorMessage}
              />
            </Box>
          )}
        </Box>
      </Box>
      {isLocked && (
        <Box width={isDevice ? '100%' : '30em'}>
          <Typography variant="body1" align="right">
            <Box color={theme.colors.error}>Email already exists</Box>
          </Typography>
          <Typography variant="body1" align="right">
            <Link
              onClick={() => {
                resendConfirmationCode()
                setSendCode(true)
                setShowConfirmEmailModal(true)
                setIsLocked(false)
              }}
            >
              <Box color={theme.colors.primary}>Resend code</Box>
            </Link>
          </Typography>
          <Typography variant="body1" align="right">
            <Box style={{ color: theme.colors.accent1, marginRight: '3em' }}> or</Box>
            <Link
              onClick={() => {
                setIsLocked(false)
                setUsername('')
                setPassword('')
              }}
            >
              <Box color={theme.colors.primary}>Continue with a new Email Address</Box>
            </Link>
          </Typography>
        </Box>
      )}
      <Box display="flex" m={isDevice ? '' : ''} width={isDevice ? '100%' : '30em'}>
        {/* @ts-ignore */}
        <Button
          design='primary'
          disabled={code.length !== 6 || timer === 0}
          fullWidth
          onClick={() => {
            if (getItem('browserClosed') === 'true') {
              clearItem('browserClosed')
            }
            confirmEmail(code)
          }}
        >
          <strong>SIGN UP</strong>
        </Button>
      </Box>
    </Box>
  )
}
